import React, { useState, useEffect } from 'react';
import { User, Plus, Search, Edit, Eye, Phone, Mail, MapPin, FileText, Briefcase, DollarSign, ChevronLeft, Save, X } from 'lucide-react';
import { supabase } from './supabaseClient';

// Separate Form Component - Completely isolated to prevent focus loss
const CustomerForm = ({ 
  formData, 
  onFormDataChange, 
  onSave, 
  onCancel, 
  saving, 
  isEditing 
}) => {
  // Local state to prevent parent re-renders from affecting inputs
  const [localFormData, setLocalFormData] = useState(formData);

  // Update local state when parent form data changes (like when editing)
  useEffect(() => {
    setLocalFormData(formData);
  }, [formData]);

  // Handle input changes locally first - THIS PREVENTS FOCUS LOSS
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...localFormData, [name]: value };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className={`${isEditing ? 'bg-blue-500' : 'bg-green-500'} text-white p-6 rounded-t-xl`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {isEditing ? <Edit size={32} /> : <Plus size={32} />}
            <h2 className="text-2xl font-bold">
              {isEditing ? 'Edit Customer' : 'New Customer'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Form Fields */}
      <div className="p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company
            </label>
            <input
              type="text"
              name="company"
              value={localFormData.company || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter company name (optional)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                value={localFormData.first_name || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                value={localFormData.last_name || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                name="phone"
                value={localFormData.phone || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="807-555-0123"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={localFormData.email || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="customer@email.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street Address *
            </label>
            <input
              type="text"
              name="address"
              value={localFormData.address || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={localFormData.city || 'Thunder Bay'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Thunder Bay"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Province *
              </label>
              <select 
                name="province"
                value={localFormData.province || 'ON'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="ON">Ontario</option>
                <option value="BC">British Columbia</option>
                <option value="AB">Alberta</option>
                <option value="SK">Saskatchewan</option>
                <option value="MB">Manitoba</option>
                <option value="QC">Quebec</option>
                <option value="NB">New Brunswick</option>
                <option value="NS">Nova Scotia</option>
                <option value="PE">Prince Edward Island</option>
                <option value="NL">Newfoundland and Labrador</option>
                <option value="YT">Yukon</option>
                <option value="NT">Northwest Territories</option>
                <option value="NU">Nunavut</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code *
              </label>
              <input
                type="text"
                name="postal_code"
                value={localFormData.postal_code || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="P7A 1B2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={localFormData.notes || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this customer..."
            />
          </div>

          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(localFormData)}
              disabled={saving}
              className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : isEditing
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-green-500 hover:bg-green-600'
              } text-white`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>{isEditing ? 'Update Customer' : 'Save Customer'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Customer Management Component
function CustomerManagement({ onNavigate }) {
  const [activeView, setActiveView] = useState('list'); // 'list', 'detail', 'new', 'edit'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    city: 'Thunder Bay',
    province: 'ON',
    postal_code: '',
    notes: ''
  });

  // Load customers from Supabase
  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      alert('Error loading customers. Please check your Supabase connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      company: '',
      phone: '',
      email: '',
      address: '',
      city: 'Thunder Bay',
      province: 'ON',
      postal_code: '',
      notes: ''
    });
  };

  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  const handleSave = async (dataToSave) => {
    // Validate required fields
    if (!dataToSave.first_name || !dataToSave.last_name || !dataToSave.phone || !dataToSave.address || !dataToSave.city || !dataToSave.postal_code) {
      alert('Please fill in all required fields (marked with *)');
      return;
    }
    
    try {
      setSaving(true);
      
      if (activeView === 'edit' && selectedCustomer) {
        // Update existing customer
        const { error } = await supabase
          .from('customers')
          .update(dataToSave)
          .eq('id', selectedCustomer.id);
        
        if (error) throw error;
        alert('Customer updated successfully!');
      } else {
        // Create new customer
        const { error } = await supabase
          .from('customers')
          .insert([dataToSave]);
        
        if (error) throw error;
        alert('Customer created successfully!');
      }
      
      // Reload customers and return to list
      await loadCustomers();
      setActiveView('list');
      resetForm();
      
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Error saving customer: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setActiveView('list');
    resetForm();
  };

  const deleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (error) throw error;
      
      alert('Customer deleted successfully!');
      await loadCustomers();
      setActiveView('list');
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Error deleting customer: ' + error.message);
    }
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer =>
    `${customer.first_name} ${customer.last_name} ${customer.company || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Show form for new or edit
  if (activeView === 'new' || activeView === 'edit') {
    return (
      <CustomerForm
        formData={formData}
        onFormDataChange={handleFormDataChange}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        isEditing={activeView === 'edit'}
      />
    );
  }

  // Customer Detail View
  if (activeView === 'detail') {
    return (
      <div className="bg-white rounded-xl shadow-lg">
        {/* Header */}
        <div className="bg-blue-500 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <User size={32} />
              <div>
                <h2 className="text-2xl font-bold">
                  {selectedCustomer?.first_name} {selectedCustomer?.last_name}
                </h2>
                <p className="text-blue-100">{selectedCustomer?.company || 'No company'}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setFormData({
                    first_name: selectedCustomer.first_name,
                    last_name: selectedCustomer.last_name,
                    company: selectedCustomer.company || '',
                    phone: selectedCustomer.phone,
                    email: selectedCustomer.email || '',
                    address: selectedCustomer.address,
                    city: selectedCustomer.city,
                    province: selectedCustomer.province,
                    postal_code: selectedCustomer.postal_code,
                    notes: selectedCustomer.notes || ''
                  });
                  setActiveView('edit');
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Edit size={20} />
                <span>Edit</span>
              </button>
              <button
                onClick={() => deleteCustomer(selectedCustomer.id)}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <X size={20} />
                <span>Delete</span>
              </button>
              <button
                onClick={() => setActiveView('list')}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <ChevronLeft size={20} />
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="text-gray-400" size={20} />
                  <span>{selectedCustomer?.phone}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="text-gray-400" size={20} />
                  <span>{selectedCustomer?.email || 'No email provided'}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="text-gray-400 mt-1" size={20} />
                  <div>
                    <p>{selectedCustomer?.address}</p>
                    <p>{selectedCustomer?.city}, {selectedCustomer?.province} {selectedCustomer?.postal_code}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Summary */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Customer Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer Since:</span>
                  <span className="font-medium">{new Date(selectedCustomer?.customer_since).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${selectedCustomer?.status === 'Active' ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedCustomer?.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer ID:</span>
                  <span className="font-medium">#{selectedCustomer?.id}</span>
                </div>
              </div>
              
              {selectedCustomer?.notes && (
                <div className="mt-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-2">Notes</h4>
                  <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedCustomer.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button onClick={() => onNavigate?.('estimating', selectedCustomer)} className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-lg flex items-center justify-center space-x-2 transition-colors">
                <FileText size={20} />
                <span>Create Estimate</span>
              </button>
              <button onClick={() => onNavigate?.('jobs', selectedCustomer)} className="bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-lg flex items-center justify-center space-x-2 transition-colors">
                <Briefcase size={20} />
                <span>View Jobs</span>
              </button>
              <button onClick={() => onNavigate?.('invoicing', selectedCustomer)} className="bg-yellow-500 hover:bg-yellow-600 text-white p-4 rounded-lg flex items-center justify-center space-x-2 transition-colors">
                <DollarSign size={20} />
                <span>View Invoices</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Customer List View (Default)
  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="bg-blue-500 text-white p-6 rounded-t-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <User size={32} />
            <div>
              <h2 className="text-2xl font-bold">Customer Management</h2>
              <p className="text-blue-100">{customers.length} customers</p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setActiveView('new');
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus size={20} />
            <span>New Customer</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-6 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search customers by name, company, phone, or email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Customer List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading customers...</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredCustomers.map(customer => (
              <div key={customer.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between gap-4">
                  <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                    <User className="text-blue-600" size={24} />
                  </div>

                  <div className="flex-shrink-0 w-52">
                    <h3 className="text-base font-semibold text-gray-800">
                      {customer.first_name} {customer.last_name}
                    </h3>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-base text-gray-600 truncate">{customer.company || 'No company'}</p>
                  </div>

                  <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0 w-36">
                    <Phone size={18} />
                    <span className="truncate">{customer.phone}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0 w-52">
                    <Mail size={18} />
                    <span className="truncate">{customer.email || 'No email'}</span>
                  </div>

                  <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0 w-36">
                    <MapPin size={18} />
                    <span className="truncate">{customer.city}, {customer.province}</span>
                  </div>

                  <div className="flex-shrink-0">
                    <span className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${customer.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {customer.status}
                    </span>
                  </div>

                  <div className="flex space-x-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setActiveView('detail');
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setFormData({
                          first_name: customer.first_name,
                          last_name: customer.last_name,
                          company: customer.company || '',
                          phone: customer.phone,
                          email: customer.email || '',
                          address: customer.address,
                          city: customer.city,
                          province: customer.province,
                          postal_code: customer.postal_code,
                          notes: customer.notes || ''
                        });
                        setActiveView('edit');
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-colors"
                      title="Edit Customer"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredCustomers.length === 0 && (
          <div className="text-center py-8">
            <User className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? 'No customers found matching your search.' : 'No customers yet. Add your first customer!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerManagement;